import React, { CSSProperties, FC, useState } from 'react';
import { Card, Select } from 'antd';
import { Text } from 'components/text/Text';
import styled from 'styled-components';
import { colors } from 'styles/colors';
import { PricingModel } from '../my-pricing.model';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';

const { Option } = Select;

const StyledPricingItemCard = styled(Card)<{
  mostSold: boolean | undefined;
  maxMobileLarge: boolean;
  width: string;
}>`
  flex: 1;
  height: 100%;
  min-width: ${({ width }) => width};
  max-width: ${({ maxMobileLarge, width }) => (maxMobileLarge ? '100%' : width)};
  margin-top: ${({ mostSold, maxMobileLarge }) =>
    !mostSold ? '26px' : maxMobileLarge ? '26px' : ''};
  border: 0;

  .ant-card-head-title {
    padding: 0;
  }
`;

const SelectButton = styled.div<{ selected: boolean | undefined }>`
  border-radius: 6px;
  color: ${colors.white};
  background: ${({ selected }) => (selected ? colors.primary : colors.violetElite)};
  padding: 3px 20px 3px 20px;
  text-align: center;
  cursor: pointer;
  font-weight: 600;
  margin: 12px 0;
`;

const DesciptionBox = styled.div<{ maxMobileLarge: boolean }>`
  padding-top: 5px;
  line-height: 14px;
  text-align: ${({ maxMobileLarge }) => (maxMobileLarge ? 'center' : '')};
`;

const CustomSelectorBox = styled.div<{ maxMobileLarge: boolean }>`
  display: flex;
  justify-content: ${({ maxMobileLarge }) => (maxMobileLarge ? 'center' : '')};
  margin: 8px 0 0 0;
`;

const GradeGuaranteeBadge = styled.div<{ disabled: boolean }>`
  border-radius: 8px;
  background-color: ${({ disabled }) => (disabled ? colors.darkGray : colors.greenBright)};
  color: ${colors.white};
  text-align: center;
  font-weight: 500;
  margin-top: 8px;
  padding-bottom: 2px;
`;

const headStyle: CSSProperties = {
  backgroundColor: colors.greenBright,
  padding: '0',
  textAlign: 'center',
  display: 'grid',
  placeItems: 'center',
  fontWeight: 600,
  color: colors.white,
  minHeight: '20px',
  height: '26px',
};

const bodyStyle: CSSProperties = {
  minHeight: '230px',
  padding: '20px',
};

export type Props = {
  pricingModel: PricingModel;
  listener: (lessonUnitCount: number) => void;
  selected?: boolean;
  onClick: () => void;
  itemWidth?: string;
  showGradeGuarantee?: boolean;
};

export const PricingItem: FC<Props> = ({
  pricingModel,
  listener,
  selected,
  onClick,
  itemWidth = '210px',
  showGradeGuarantee = true,
}) => {
  const [customSelectedUnitCount, setCustomSelectedUnitCount] = useState(16);
  const { maxMobileLarge } = useBreakpoints();

  const customSelectorChange = (value: number) => {
    setCustomSelectedUnitCount(value * 4);
    listener(value * 4);
    onClick();
  };

  return (
    <StyledPricingItemCard
      mostSold={pricingModel.mostSold}
      maxMobileLarge={maxMobileLarge}
      title={
        pricingModel.mostSold
          ? translate(translationKeys.pricing.pricingModel.default.mostSold)
          : ''
      }
      headStyle={pricingModel.mostSold ? headStyle : undefined}
      bodyStyle={bodyStyle}
      width={itemWidth}
    >
      <div>
        <Text color={'primaryLight'} size={20} fontWeight={700}>
          {pricingModel.title}
        </Text>
      </div>
      {!pricingModel.lessonPerWeek && (
        <CustomSelectorBox maxMobileLarge={maxMobileLarge}>
          <Select defaultValue={4} onChange={(value: number) => customSelectorChange(value)}>
            <Option value={4}>4</Option>
            <Option value={5}>5</Option>
            <Option value={6}>6</Option>
            <Option value={7}>7</Option>
            <Option value={8}>8</Option>
          </Select>
          <Text
            fontWeight={600}
            style={{ maxWidth: '90px', marginLeft: '10px', textAlign: 'left' }}
          >
            {pricingModel.lessonPerWeek}{' '}
            {translate(translationKeys.pricing.pricingModel.default.customSelector)}
          </Text>
        </CustomSelectorBox>
      )}
      <div
        style={{
          height: '48px',
          display: pricingModel.lessonPerWeek ? '' : 'none',
          margin: '8px 0 0 0',
        }}
      >
        <div>
          {pricingModel.lessonPerWeek && (
            <Text fontWeight={600}>
              {translate('pricing.pricingModel.default.lesson', {
                count: parseInt(pricingModel.lessonPerWeek),
              })}
            </Text>
          )}
        </div>
        {pricingModel.lessonPerWeek && (
          <div>
            <Text fontWeight={600}>
              {translate(translationKeys.pricing.pricingModel.default.perWeek)}
            </Text>
          </div>
        )}
      </div>
      <SelectButton
        selected={selected}
        onClick={() => {
          listener(pricingModel.preSelectedlessonUnitCount || customSelectedUnitCount);
          onClick();
        }}
      >
        {selected
          ? translate(translationKeys.pricing.pricingModel.default.selectedPlan)
          : translate(translationKeys.pricing.pricingModel.default.selectPlan)}
      </SelectButton>
      <DesciptionBox maxMobileLarge={maxMobileLarge}>
        <Text size={12}>{pricingModel.description}</Text>
      </DesciptionBox>
      {showGradeGuarantee && (
        <GradeGuaranteeBadge disabled={!pricingModel.gradeGuarantee}>
          {pricingModel.gradeGuarantee
            ? translate(translationKeys.pricing.pricingModel.default.gradeGuarantee)
            : translate(translationKeys.pricing.pricingModel.default.noGradeGuarantee)}
        </GradeGuaranteeBadge>
      )}
    </StyledPricingItemCard>
  );
};
